<template>
  <Teleport to="body">
    <div class="ai-panel" :class="{ open }" v-show="open">
    <div class="ai-panel-header">
      <div class="ai-title">
        <span class="ai-dot"></span>
        AI 비서에게 요청
      </div>
      <div class="ai-actions">
        <button class="ai-icon-btn" @click="reset" title="새 대화">↻</button>
        <button class="ai-icon-btn" @click="$emit('close')" title="닫기">✕</button>
      </div>
    </div>

    <div class="ai-msg-list" ref="listEl">
      <div v-if="messages.length === 0" class="ai-hint">
        예) "아메리카노 4500원으로 추가해줘", "콜드브루를 음료 카테고리에 연결해줘",<br/>
        "주류에 소주 3000원, 맥주 2000원 등록" (여러 개 한 번에),<br/>
        "콜드브루 품절", "전체 메뉴 10% 할인", "디저트 빼고 5% 할인"<br/>
        🖼 이미지를 첨부해 "이 사진을 콜드브루 이미지로 적용", "메뉴판 사진의 상품 모두 등록" 도 가능
      </div>
      <div v-for="(m, i) in messages" :key="i" :class="['ai-msg', m.role]">
        <div v-if="m.role === 'user'" class="bubble user">
          <img v-if="m.image" :src="m.image" class="msg-image" alt="" />
          <div v-if="m.text">{{ m.text }}</div>
        </div>
        <div v-else-if="m.role === 'assistant'" class="bubble assistant">
          <div v-if="m.text" class="bubble-text">{{ m.text }}</div>

          <!-- ask 후보 버튼 -->
          <div v-if="m.candidates && m.candidates.length" class="cand-list">
            <button v-for="c in m.candidates" :key="c" class="cand-btn" @click="pickCandidate(c)">{{ c }}</button>
          </div>

          <!-- 확인 대기 중인 plan (한 개 이상의 작업) -->
          <div v-if="m.plan && (m.planStatus === 'pending' || m.planStatus === 'executing')" class="plan-card">
            <div v-if="m.plan.steps.length > 1" class="plan-count">{{ m.plan.steps.length }}개 작업</div>
            <div v-for="(step, si) in m.plan.steps" :key="si" class="plan-step">
              <div class="plan-action">
                <span class="action-label">{{ step.action_label || step.action }}</span>
              </div>
              <ul class="plan-fields" v-if="step.fields && step.fields.length">
                <li v-for="row in step.fields" :key="row.k">
                  <b>{{ row.k }}</b>
                  <span>{{ row.v }}</span>
                </li>
              </ul>
            </div>
            <div class="plan-buttons">
              <button class="btn-confirm" @click="confirmPlan(i)" :disabled="m.planStatus === 'executing'">
                {{ m.planStatus === 'executing' ? '실행 중...' : (m.plan.steps.length > 1 ? m.plan.steps.length + '건 모두 실행' : '확인하고 실행') }}
              </button>
              <button class="btn-cancel" @click="cancelPlan(i)" :disabled="m.planStatus === 'executing'">취소</button>
            </div>
          </div>

          <!-- 실행 결과 -->
          <div v-else-if="m.planStatus === 'done'" class="plan-result-wrap">
            <template v-if="m.results && m.results.length">
              <div v-if="m.summary && m.results.length > 1" class="plan-result-summary">{{ m.summary }}</div>
              <div v-for="(res, ri) in m.results" :key="ri" class="plan-result">
                <span v-if="res.ok" class="ok">✓ {{ res.summary || ((res.action_label || res.action) + ' 완료') }}</span>
                <span v-else class="err">✕ {{ (res.action_label || res.action) }} 실패: {{ res.error }}</span>
              </div>
            </template>
            <div v-else class="plan-result">
              <span v-if="m.error" class="err">실패: {{ m.error }}</span>
              <span v-else class="ok">✓ {{ m.summary || '완료' }}</span>
            </div>
          </div>
          <div v-else-if="m.planStatus === 'cancelled'" class="plan-cancelled">
            <span>취소됨</span>
          </div>
        </div>
      </div>
      <div v-if="loading" class="ai-msg assistant">
        <div class="bubble assistant"><span class="dots"><i></i><i></i><i></i></span></div>
      </div>
    </div>

    <!-- 첨부 이미지 미리보기 -->
    <div v-if="attachedImage" class="attach-preview" @click.stop @mousedown.stop>
      <img :src="attachedImage.preview || attachedImage.url" alt="" />
      <span class="attach-name">
        <span class="attach-fname">{{ attachedImage.name }}</span>
        <small class="attach-hint">첨부 유지 중 — 같은 이미지로 계속 질문 가능 (✕ 로 해제)</small>
      </span>
      <button class="attach-remove" @click="removeAttachedImage" title="이미지 첨부 해제">✕</button>
    </div>
    <div v-else-if="uploading" class="attach-uploading">🖼 이미지 업로드 중...</div>

    <div class="ai-input-row" @click.stop @mousedown.stop>
      <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="onPickImage" />
      <button class="attach-btn" @click="triggerFilePick" :disabled="loading || uploading" title="이미지 첨부">🖼</button>
      <textarea
          ref="ta"
          v-model="draft"
          @keydown="onKeydown"
          @blur="onBlur"
          rows="2"
          placeholder="메뉴 작업을 자연어로 입력하세요. Enter=전송, Shift+Enter=줄바꿈"
          :disabled="loading"
      />
      <button class="send-btn" @click="send" :disabled="loading || uploading || !draft.trim()">전송</button>
    </div>
    </div>
  </Teleport>
</template>

<script>
import { plan as planCall, executeBatch, uploadImage } from './aiClient'

export default {
  name: 'AiChatPanel',
  props: {
    open: { type: Boolean, default: false },
  },
  data: () => ({
    draft: '',
    loading: false,
    uploading: false,
    attachedImage: null,   // { url, name, preview }
    messages: [],
  }),
  watch: {
    open(v) {
      if (v) {
        this.installGlobalFocusTrap()
        this.$nextTick(() => this.$refs.ta?.focus({ preventScroll: true }))
      } else {
        this.removeGlobalFocusTrap()
      }
    },
  },
  beforeUnmount() {
    this.removeGlobalFocusTrap()
  },
  methods: {
    reset() {
      this.messages = []
      this.draft = ''
      this.removeAttachedImage()
    },
    triggerFilePick() {
      if (this.$refs.fileInput) this.$refs.fileInput.click()
    },
    async onPickImage(e) {
      const file = e.target.files && e.target.files[0]
      if (e.target) e.target.value = ''   // 같은 파일 재선택 허용
      if (!file) return
      if (!file.type || !file.type.startsWith('image/')) {
        this.messages.push({ role: 'assistant', text: '이미지 파일만 첨부할 수 있어요.' })
        return
      }
      // 이전 첨부 정리 후 로컬 미리보기 생성
      this.removeAttachedImage()
      const preview = URL.createObjectURL(file)
      this.uploading = true
      this.$nextTick(this.scrollBottom)
      try {
        const res = await uploadImage(file)
        if (res && res.ok && res.url) {
          this.attachedImage = { url: res.url, name: file.name, preview }
        } else {
          URL.revokeObjectURL(preview)
          this.messages.push({ role: 'assistant', text: '이미지 업로드 실패: ' + (res && res.error ? res.error : '알 수 없는 오류') })
        }
      } catch (err) {
        URL.revokeObjectURL(preview)
        this.messages.push({ role: 'assistant', text: '이미지 업로드 오류: ' + this.formatError(err) })
      } finally {
        this.uploading = false
        this.$nextTick(this.scrollBottom)
      }
    },
    removeAttachedImage() {
      if (this.attachedImage && this.attachedImage.preview) {
        try { URL.revokeObjectURL(this.attachedImage.preview) } catch (e) { /* noop */ }
      }
      this.attachedImage = null
    },
    formatArgs(args) {
      try {
        return JSON.stringify(args, null, 2)
      } catch {
        return String(args)
      }
    },
    formatResult(r) {
      if (!r) return 'ok'
      const parts = ['ok']
      if (r.id != null) parts.push('id ' + r.id)
      if (r.affected != null) parts.push(r.affected + '건')
      // bulk-discount sample 표시
      if (Array.isArray(r.sample) && r.sample.length > 0) {
        const lines = r.sample.map(s =>
            `${s.title}: ${s.org_price ?? s.old_price}→${s.price ?? s.new_price}원`
        )
        return parts.join(' · ') + '\n' + lines.join('\n')
      }
      return parts.join(' · ')
    },
    formatError(e) {
      if (typeof e === 'string') return e
      if (e?.response?.data?.message) return e.response.data.message
      if (e?.message) return e.message
      try { return JSON.stringify(e) } catch { return String(e) }
    },
    async send() {
      const text = this.draft.trim()
      if (!text || this.loading || this.uploading) return

      const img = this.attachedImage
      const userMsg = { role: 'user', text }
      // 말풍선 썸네일은 호스팅 URL 사용 (objectURL revoke 와 무관하게 유지)
      if (img) userMsg.image = img.url
      this.messages.push(userMsg)
      this.draft = ''
      const imageUrl = img ? img.url : null
      // 이미지는 첨부 상태로 유지(sticky) → 같은 이미지로 후속 질문(비교·수정 등) 가능.
      // 사용자가 ✕ 또는 '새 대화'로 제거할 때까지 다음 요청에도 함께 전송된다.
      this.loading = true
      this.$nextTick(this.scrollBottom)

      const history = this.messages
          .filter(m => m.text)
          .map(m => ({ role: m.role, text: m.text }))
          .slice(-20)

      try {
        const p = await planCall(text, history, imageUrl)

        const reply = {
          role: 'assistant',
          text: p.say || '',
        }

        // actions 배열(다건) 우선, 없으면 단일 action 을 1개짜리 배열로 정규화
        const steps = (Array.isArray(p.actions) && p.actions.length)
            ? p.actions
            : (p.action && p.requires_confirm
                ? [{ action: p.action, action_label: p.action_label, args: p.args, fields: p.fields }]
                : [])

        if (p.requires_confirm && steps.length) {
          reply.plan = { steps }
          reply.planStatus = 'pending'
          if (!reply.text) reply.text = steps.length > 1 ? ('아래 ' + steps.length + '개 작업을 실행할까요?') : '아래 작업을 실행할까요?'
        } else if (Array.isArray(p.candidates) && p.candidates.length) {
          reply.candidates = p.candidates
        } else if (!reply.text) {
          reply.text = '응답을 인식하지 못했어요.'
        }

        this.messages.push(reply)
      } catch (e) {
        this.messages.push({
          role: 'assistant',
          text: '오류: ' + this.formatError(e),
        })
      } finally {
        this.loading = false
        this.$nextTick(this.scrollBottom)
      }
    },
    async confirmPlan(idx) {
      const m = this.messages[idx]
      if (!m || !m.plan) return
      if (m.planStatus !== 'pending') return  // 중복 실행 차단
      const steps = m.plan.steps || []
      if (!steps.length) return
      m.planStatus = 'executing'
      this.loading = true
      try {
        const r = await executeBatch(steps)
        m.planStatus = 'done'
        m.results = (r && Array.isArray(r.results)) ? r.results : null
        m.summary = r ? r.summary : ''
        if (!m.results && !(r && r.ok)) m.error = (r && r.error) || '실행 실패'
        // 성공한 작업이 하나라도 있으면 그리드/리스트 새로고침 알림
        const anyOk = (r && r.ok) || (m.results && m.results.some(x => x && x.ok))
        if (anyOk && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('ai:applied', { detail: { actions: steps.map(s => s.action) } }))
        }
      } catch (e) {
        m.planStatus = 'done'
        m.error = this.formatError(e)
      } finally {
        this.loading = false
        this.$nextTick(this.scrollBottom)
      }
    },
    cancelPlan(idx) {
      const m = this.messages[idx]
      if (!m || !m.plan) return
      m.planStatus = 'cancelled'
    },
    pickCandidate(text) {
      this.draft = text
      this.send()
    },
    onKeydown(e) {
      // Enter 만 (Shift+Enter 는 줄바꿈)
      if (e.key !== 'Enter' || e.shiftKey) return
      // 한글 IME 조합 중이면 무시 (조합 완료 위해 Enter 가 필요)
      if (e.isComposing || e.keyCode === 229) return
      e.preventDefault()
      this.send()
    },
    scrollBottom() {
      const el = this.$refs.listEl
      if (el) el.scrollTop = el.scrollHeight
    },
    onBlur(e) {
      if (!this.open) return
      // 패널 내부의 다른 인터랙티브 요소 (확인/취소 버튼 등) 로 이동한 경우엔 그대로 둔다.
      const next = e.relatedTarget
      const panelEl = this.$refs.ta?.closest?.('.ai-panel')
      if (next && panelEl && panelEl.contains(next)) return
      // 패널 밖으로 빠진 경우 (그리드/스캐너 등이 가로챈 경우) 즉시 복귀
      requestAnimationFrame(() => {
        if (this.$refs.ta && this.open && document.activeElement !== this.$refs.ta) {
          this.$refs.ta.focus({ preventScroll: true })
        }
      })
    },
    installGlobalFocusTrap() {
      if (this._focusTrapInstalled) return
      this._focusTrapInstalled = true
      this._focusinHandler = (ev) => {
        if (!this.open) return
        const target = ev.target
        const panelEl = this.$refs.ta?.closest?.('.ai-panel')
        if (!panelEl) return
        // 포커스가 패널 외부로 빠졌는데 사용자가 명시적으로 다른 input 에 클릭한 게 아니면 textarea 로 복귀
        if (!panelEl.contains(target)) {
          // body 같은 비-인터랙티브 타겟이면 즉시 복귀, input/textarea 면 사용자가 의도적으로 클릭한 것이므로 두자
          const isInteractiveElsewhere = target?.matches?.('input,textarea,select,[contenteditable="true"],[contenteditable=true]')
          if (!isInteractiveElsewhere) {
            requestAnimationFrame(() => {
              if (this.$refs.ta && this.open) this.$refs.ta.focus({ preventScroll: true })
            })
          }
        }
      }
      document.addEventListener('focusin', this._focusinHandler, true)
    },
    removeGlobalFocusTrap() {
      if (this._focusinHandler) {
        document.removeEventListener('focusin', this._focusinHandler, true)
        this._focusinHandler = null
      }
      this._focusTrapInstalled = false
    },
  },
}
</script>

<style scoped>
.ai-panel {
  position: fixed;
  right: 24px;
  bottom: 96px;
  width: 440px;
  max-width: calc(100vw - 32px);
  height: min(680px, 80vh);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  z-index: 9998;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}
@media (max-width: 480px) {
  .ai-panel { right: 8px; bottom: 80px; }
}
.ai-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: #fff;
}
.ai-title {
  font-weight: 600;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ai-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 6px rgba(74, 222, 128, 0.7);
}
.ai-actions { display: flex; gap: 4px; }
.ai-icon-btn {
  background: rgba(255,255,255,0.18);
  border: none;
  color: #fff;
  width: 28px; height: 28px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}
.ai-icon-btn:hover { background: rgba(255,255,255,0.32); }

.ai-msg-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f9fafb;
}
.ai-hint {
  color: #9ca3af;
  font-size: 12.5px;
  line-height: 1.6;
  background: #fff;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px dashed #e5e7eb;
}

.ai-msg { display: flex; }
.ai-msg.user { justify-content: flex-end; }
.ai-msg.assistant { justify-content: flex-start; }

.bubble {
  max-width: 92%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}
.bubble.user {
  background: #4f46e5;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.bubble.assistant {
  background: #fff;
  color: #1f2937;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 4px;
}
.bubble-text { margin-bottom: 6px; }

.plan-card {
  margin-top: 8px;
  border: 1px solid #e0e7ff;
  background: #f5f7ff;
  border-radius: 10px;
  padding: 10px;
}
.plan-action { margin-bottom: 8px; }
.action-label {
  display: inline-block;
  background: #4338ca;
  color: #fff;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}
.plan-fields {
  list-style: none;
  margin: 0;
  padding: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
}
.plan-fields li {
  display: flex;
  padding: 6px 10px;
  font-size: 12.5px;
  border-bottom: 1px solid #f3f4f6;
}
.plan-fields li:last-child { border-bottom: none; }
.plan-fields li b {
  width: 110px;
  color: #6b7280;
  font-weight: 500;
  flex-shrink: 0;
}
.plan-fields li span {
  flex: 1;
  color: #1f2937;
  word-break: break-all;
}
.plan-buttons {
  display: flex;
  gap: 6px;
  margin-top: 8px;
}
.btn-confirm {
  flex: 1;
  background: #4f46e5;
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 12.5px;
  cursor: pointer;
}
.btn-confirm:hover { background: #4338ca; }
.btn-cancel {
  background: #fff;
  color: #6b7280;
  border: 1px solid #d1d5db;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12.5px;
  cursor: pointer;
}
.btn-cancel:hover { background: #f3f4f6; }

.plan-result, .plan-cancelled {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 12px;
  margin-top: 6px;
  white-space: pre-wrap;
}
.tag {
  background: #eef2ff;
  color: #4338ca;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: ui-monospace, monospace;
  font-size: 11px;
}
.ok { color: #059669; font-weight: 500; white-space: pre-wrap; }
.err { color: #dc2626; }
.plan-cancelled { color: #9ca3af; }

/* 다건 작업 */
.plan-count {
  font-size: 11px;
  font-weight: 600;
  color: #4338ca;
  margin-bottom: 6px;
}
.plan-step + .plan-step { margin-top: 8px; padding-top: 8px; border-top: 1px dashed #d8def7; }
.plan-result-wrap { margin-top: 6px; }
.plan-result-summary { font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 4px; }
.plan-result + .plan-result { margin-top: 2px; }

/* 사용자 메시지 / 첨부 이미지 */
.msg-image {
  display: block;
  max-width: 180px;
  max-height: 180px;
  border-radius: 8px;
  margin-bottom: 6px;
  object-fit: cover;
}
.attach-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid #e5e7eb;
  background: #f5f7ff;
}
.attach-preview img {
  width: 40px; height: 40px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e0e7ff;
}
.attach-name {
  flex: 1;
  font-size: 12px;
  color: #4338ca;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.attach-fname {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.attach-hint { color: #9ca3af; font-size: 10.5px; }
.attach-remove {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 13px;
}
.attach-remove:hover { color: #dc2626; }
.attach-uploading {
  padding: 8px 12px;
  border-top: 1px solid #e5e7eb;
  background: #f5f7ff;
  font-size: 12px;
  color: #6b7280;
}
.attach-btn {
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  width: 38px;
  font-size: 16px;
  cursor: pointer;
  flex-shrink: 0;
}
.attach-btn:hover:not(:disabled) { background: #f3f4f6; border-color: #4f46e5; }
.attach-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.cand-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}
.cand-btn {
  text-align: left;
  background: #fff;
  border: 1px solid #c7d2fe;
  color: #4338ca;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}
.cand-btn:hover {
  background: #eef2ff;
  border-color: #818cf8;
}

.dots i {
  display: inline-block;
  width: 6px; height: 6px;
  background: #9ca3af;
  border-radius: 50%;
  margin: 0 2px;
  animation: dotbounce 1.2s infinite ease-in-out;
}
.dots i:nth-child(2) { animation-delay: 0.15s; }
.dots i:nth-child(3) { animation-delay: 0.3s; }
@keyframes dotbounce {
  0%,80%,100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
}

.ai-input-row {
  display: flex;
  padding: 10px 12px;
  border-top: 1px solid #e5e7eb;
  background: #fff;
  gap: 8px;
}
.ai-input-row textarea {
  flex: 1;
  resize: none;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 13px;
  outline: none;
  font-family: inherit;
}
.ai-input-row textarea:focus { border-color: #4f46e5; }
.send-btn {
  background: #4f46e5;
  color: #fff;
  border: none;
  padding: 0 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}
.send-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
